import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tooltip, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Visibility as VisibilityIcon, AssignmentLate as AssignmentLateIcon } from '@mui/icons-material';
import { mergeDataGridStyles } from '../../styles/dataGridStyles';
import incidentService from '../../services/incidentService';

const IncidentDraftList = () => {
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadIncidents();

        // Назначение приезжает синком — после него список пересобираем.
        const onSyncComplete = () => loadIncidents();
        window.addEventListener('syncComplete', onSyncComplete);
        return () => window.removeEventListener('syncComplete', onSyncComplete);
    }, []);

    const loadIncidents = async () => {
        setLoading(true);
        try {
            setIncidents(await incidentService.listAll());
        } catch (error) {
            console.error('Error loading incidents:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = async () => {
        try {
            const newDraft = await incidentService.createDraft();
            navigate(`/incidents/${newDraft.uuid}/edit`);
        } catch (error) {
            console.error('Error creating draft:', error);
        }
    };

    const handleOpen = (uuid) => {
        navigate(`/incidents/${uuid}/edit`);
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedId !== null) {
            try {
                await incidentService.deleteIncident(selectedId);
                await loadIncidents();
            } catch (error) {
                console.error('Error deleting draft:', error);
            }
        }
        setDeleteDialogOpen(false);
        setSelectedId(null);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (Number.isNaN(date.getTime())) return '-';
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'America/New_York'
            });
        } catch {
            return '-';
        }
    };

    const actionRequiredCount = incidents.filter(i => incidentService.needsMyAction(i)).length;

    const columns = [
        {
            field: 'siNumber',
            headerName: 'SI #',
            width: 110,
            valueGetter: (_, row) => row.siNumber || '—'
        },
        {
            field: 'incidentDateTime',
            headerName: 'Incident Date & Time',
            flex: 1,
            minWidth: 170,
            valueGetter: (_, row) => {
                const dateTime = row.payload?.incidentDateTime;
                return dateTime ? formatDateTime(dateTime) : 'Not set';
            }
        },
        {
            field: 'incidentType',
            headerName: 'Type',
            flex: 1,
            minWidth: 150,
            valueGetter: (_, row) => {
                const types = row.payload?.incidentTypes;
                if (Array.isArray(types) && types.length > 0) {
                    return types.join(', ');
                }
                return row.payload?.incidentType || 'Not set';
            }
        },
        {
            field: 'location',
            headerName: 'Location',
            flex: 1,
            minWidth: 150,
            valueGetter: (_, row) => row.payload?.location?.location || '—'
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 170,
            sortable: false,
            renderCell: (params) => {
                const state = incidentService.getUiState(params.row);
                return (
                    <Tooltip title={state.hint}>
                        <Chip
                            size="small"
                            color={state.color}
                            variant={state.actionRequired ? 'filled' : 'outlined'}
                            icon={state.actionRequired ? <AssignmentLateIcon /> : undefined}
                            label={state.label}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: 'assignedToName',
            headerName: 'Assigned To',
            flex: 1,
            minWidth: 150,
            valueGetter: (_, row) => row.assignedToName || '—'
        },
        {
            field: 'updatedAt',
            headerName: 'Updated At',
            width: 170,
            valueGetter: (_, row) => formatDateTime(row.updatedAt)
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 110,
            sortable: false,
            renderCell: (params) => {
                const canEdit = incidentService.needsMyAction(params.row)
                    || (params.row.status === 'processing' && !params.row.backendId);
                const canDelete = incidentService.canDelete(params.row);

                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpen(params.row.uuid);
                            }}
                            color="primary"
                            title={canEdit ? 'Continue' : 'View report'}
                        >
                            {canEdit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                        {canDelete && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(params.row.id);
                                }}
                                color="error"
                                title="Delete draft"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                );
            }
        }
    ];

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    flex: '0 0 auto'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Incidents</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Your reports and incidents assigned to you
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {incidents.length} record{incidents.length !== 1 ? 's' : ''}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        New Report
                    </Button>
                </Box>
            </Box>

            {actionRequiredCount > 0 && (
                <Alert severity="warning" sx={{ mx: 2, mt: 2 }} icon={<AssignmentLateIcon />}>
                    {actionRequiredCount} incident{actionRequiredCount !== 1 ? 's are' : ' is'} assigned to you and
                    {' '}need{actionRequiredCount === 1 ? 's' : ''} to be completed.
                </Alert>
            )}

            {/* DataGrid Container */}
            <Box
                sx={{
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    p: 2
                }}
            >
                <DataGrid
                    rows={incidents}
                    columns={columns}
                    loading={loading}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25 }
                        }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    getRowClassName={(params) => incidentService.needsMyAction(params.row) ? 'row-action-required' : ''}
                    onRowClick={(params) => handleOpen(params.row.uuid)}
                    rowHeight={52}
                    sx={mergeDataGridStyles({
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'pointer',
                            backgroundColor: 'action.hover',
                        },
                        '& .row-action-required': {
                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        },
                    })}
                />
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Draft?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this draft? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IncidentDraftList;
